/* eslint-disable react-hooks/rules-of-hooks */
import { useState, useEffect, useMemo, useRef } from 'react';
import Head from 'next/head';
import Router from 'next/router';

// Components
import ModalCheckout from 'components/CourseDescription/ModalCheckout';
import Breadcrumb from 'components/Breadcrumb';
import CardForBundle from 'components/CourseDescription/CardForBundle';
import CustomPagination from 'components/Pagination';
import ModalCourse from 'components/CourseDescription/ModalCourse';
import Accordion from 'components/Accordion';
import CourseData from 'components/CourseDescription/CourseData';
import CollapsibleSection from 'components/CourseDescription/Section/CollapsibleSection';
import TestimonialSection from 'components/CourseDescription/Section/TestimonialSection';
import RecommendedCourses from 'components/CourseDescription/Section/RecommendedCourses';

// Icons
import ICONS from 'utils/icons';
import { Spinner, Row } from 'react-bootstrap';

// Services
import { useImageHandler } from 'utils/custom_hooks';
import { month } from 'utils/month';
import { generateCourseDuration, generateCourseTestimoni } from 'utils/course_description';
import { synchronizedSingleCartData } from 'utils/manipulate_array';
import { generateCoverImageUrl } from 'utils/generate_cover_image_url';

import { truncateText } from 'utils/helper';
import { getClassNameByLang } from 'utils/getClassByLang';
import Button from 'components/Button';
import { BUTTON_TYPE } from 'constants/button';
import { Heading, SubHeading, TextBody } from 'components/Typography/General';
import { FONT_TYPES } from 'variables/font_groups';
import { isMobile } from 'react-device-detect';
import { getDirectionByLanguage } from 'utils/get_direction';
import { trackAddToCart, trackCtaClick, trackRemoveFromCart, trackSectionToggle, trackViewItem } from 'lib/analytics';
import ModalWrapper from 'components/ModalWrapper';
import { getProviderFromHost } from 'utils/get_provider_from_host';

// Import should be using require() instead of import because this will be used in server side
const {
  getCourseDetail,
  getCourseTestimonial,
  getCourseRecommendation,
  getCourseFaq,
} = require('services/course-description');

const LMS_PROVIDERS = ['faei', 'lmsl'];
const PROVIDER_CODE_TO_SUBDOMAIN = {
  '639a5d4dc4fa7f8778beb5d8': 'lmsl', // LMSL
  '645c38d3feceaaee6e585198': 'faei', // FAEI
};
const baseDomain = process.env.NEXT_PUBLIC_BASE_DOMAIN || process.env.NEXT_PUBLIC_MARKETPLACE_BASE_URL;

export async function getServerSideProps(context) {
  const { req, query } = context;
  const { courseId } = query;

  const courseDetail = await getCourseDetail(courseId);
  const baseUrl = `${req.protocol || 'https'}://${req.headers.host}`;

  if (!courseDetail) {
    return {
      notFound: true,
    };
  }

  const id = courseDetail?._id || '';
  const coursesId = courseDetail?.courseId;
  const courseFaq = await getCourseFaq(coursesId);
  const courseTestimonial = await getCourseTestimonial(coursesId);
  let activeTestimonial = courseTestimonial[0].testimoni[0]._id || null;

  const isBaseDomain = baseUrl === process.env.NEXT_PUBLIC_BASE_PATH;
  const courseRecommendation = await getCourseRecommendation(
    id,
    isBaseDomain ? null : courseDetail.courseOrganizer.code
  );
  const baseImageUrl = process.env.API_LMS_URL ? process.env.API_LMS_URL : '';
  return {
    props: {
      courseDetail,
      courseRecommendation,
      courseTestimonial,
      baseImageUrl,
      courseFaq: courseFaq || [],
      activeTestimonial,
      url: `${baseUrl}${req.url}`,
    },
  };
}

const totalShowedTestimony = 5;
const CourseDescription = (props) => {
  const hasTrackedRef = useRef(false);
  const { courseFaq, courseDetail, courseRecommendation, courseTestimonial, baseImageUrl, activeTestimonial, url } =
    props;

  useEffect(() => {
    if (!courseDetail || hasTrackedRef.current) return;

    trackViewItem({
      page_type: 'course_detail',
      items: [
        {
          item_id: courseDetail?.courseId || courseDetail?.productId,
          item_name: courseDetail?.courseTitle,
          item_category: courseDetail?.categoryName || 'unknown',
          price: parseFloat(
            courseDetail?.price?.$numberDecimal ? courseDetail?.price?.$numberDecimal : courseDetail?.price || 0
          ),
        },
      ],
    });

    hasTrackedRef.current = true;
  }, [courseDetail]);

  const {
    courseId,
    courseTitle,
    language,
    courseAudience,
    courseInclusion,
    prerequisite,
    certificateAwarded,
    price,
    duration,
    expectedOutcomes,
    picture,
    courseOutline,
    courseOrganizer,
    description,
    categoryName,
    levelCourse,
    subCategoryName,
    isBundling,
    listCourse,
    scheduleDate,
    scheduleTime,
    city,
    discount,
    learningProgram,
    deliveryType,
    timezone,
    facilitator,
    purchaseOption,
  } = courseDetail;

  const [faqItem, setFaqItem] = useState([]);
  const [openFaqItem, setOpenFaqItem] = useState(false);

  const [openModalCheckout, setOpenModalCheckout] = useState(false);
  const [openModalCourse, setOpenModalCourse] = useState(false);
  const [openedCollapsibles, setOpenedCollapsibles] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState({});
  const [recommendedExpanded, setIsRecommendedExpanded] = useState(false);
  const [cart, setCart] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [delivery, setDelivery] = useState(null);

  const [lastIndexShownTesti, setLastIndexShownTesti] = useState(5);
  const [customerActive, setCustomerActive] = useState(activeTestimonial);

  const [providerMismatch, setProviderMismatch] = useState(false);
  const [currentProvider, setCurrentProvider] = useState(null);

  useEffect(() => {
    if (!courseDetail) return;

    const providerFromUrl = getProviderFromHost();
    if (!providerFromUrl) return;

    const normalizedUrlProvider = providerFromUrl.toLowerCase();
    if (!LMS_PROVIDERS.includes(normalizedUrlProvider)) return;

    const providerCode = courseDetail.courseOrganizer?.code;
    if (!providerCode) return;

    const courseProviderSubdomain = PROVIDER_CODE_TO_SUBDOMAIN[providerCode];

    if (!courseProviderSubdomain) return;

    if (normalizedUrlProvider !== courseProviderSubdomain) {
      setCurrentProvider(providerFromUrl);
      setProviderMismatch(true);
    }
  }, [courseDetail]);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: Math.ceil(listCourse.length / 6),
    per_page: 6,
    total_pages: Math.ceil(listCourse.length / 6),
    totals: listCourse.length,
  });

  const [showedCourse, setShowedCourse] = useState(listCourse && listCourse.slice(0, pagination.per_page));
  const memoizedOptionList = useMemo(
    () => [courseOrganizer.logo, courseOrganizer.companyLogo],
    [courseOrganizer.logo, courseOrganizer.companyLogo]
  );
  const { currentImage, handleIsImageAvailable, handleImageToDefault } = useImageHandler({
    optionList: memoizedOptionList,
    data: courseDetail,
  });

  // generate course duration
  const courseDuration = generateCourseDuration(duration);

  // Testimoni
  const courseTestimoni = generateCourseTestimoni(courseTestimonial, baseImageUrl);

  // Faq Section
  let faqs = [];

  if (courseFaq.length > 0) {
    faqs = courseFaq.map((row) => {
      return {
        ...row,
        isOpen: false,
      };
    });
  }

  // Course outline filename
  const coursOutlineFileName =
    courseOutline.outline == 'f' ? courseOutline.value.split(/[/ ]+/).pop() : courseOutline.value;

  // validation for course is already in cart or no
  const isAddedToCart = cart.filter((data_cart) => data_cart.itemId == courseId).length !== 0;

  const handleDeliveryType = (id) => {
    if (id == '63313668e9a83d2170183b89') {
      setDelivery('Blended Learning');
    } else if (id === '63313659e9a83d2170183b87') {
      setDelivery('Classroom');
    } else if (id === '6331363fe9a83d2170183b85') {
      setDelivery('Live Online');
    } else if (id === '63313626e9a83d2170183b83') {
      setDelivery('Self Paced');
    }
  };

  const handleAddToCart = async (data) => {
    // data.name = data.courseTitle;
    const newCart = synchronizedSingleCartData({ item: data, isProduct: false });

    setIsLoading(true);
    let list_cart = [...cart];
    let new_data = [];
    const price = parseFloat(data?.price?.$numberDecimal ? data?.price?.$numberDecimal : data?.price || 0);

    if (list_cart.filter((cart) => cart.itemId == newCart.itemId).length !== 0) {
      new_data = list_cart.filter((cart) => cart.itemId !== newCart.itemId);
      trackCtaClick({
        page_type: 'course_detail',
        cta_label: 'remove_from_cart',
        location: isBundling ? 'bundle_course_detail' : 'single_course_detail',
        course_id: data.courseId || data.productId,
      });

      trackRemoveFromCart({
        page_type: 'course_detail',
        currency: 'USD',
        value: price * -1,
        items: [
          {
            item_id: data.courseId || data.productId,
            item_name: data.name,
            item_category: data.categoryName || 'unknown',
            item_variant: 'course',
            price,
            quantity: 0,
          },
        ],
        course_id: data.courseId || data.productId,
      });
      Router.reload(window.location.pathname);
    } else {
      trackCtaClick({
        page_type: 'course_detail',
        cta_label: 'add_to_cart',
        location: isBundling ? 'bundle_course_detail' : 'single_course_detail',
        course_id: data.courseId || data.productId,
      });

      trackAddToCart({
        page_type: 'course_detail',
        currency: 'USD',
        value: price,
        items: [
          {
            item_id: data.courseId || data.productId,
            item_name: data.name,
            item_category: data.categoryName || 'unknown',
            item_variant: 'course',
            price,
            quantity: 1,
          },
        ],
        course_id: data.courseId || data.productId,
      });
      list_cart.forEach((list) => {
        new_data.push(list);
      });
      new_data.push(newCart);
      setOpenModalCheckout(true);
    }

    const synchronizedCartData = new_data.map((item) => {
      return synchronizedSingleCartData({ item: item, isProduct: item.isProduct || false });
    });

    setCart(synchronizedCartData);
    localStorage.setItem('cart', JSON.stringify(synchronizedCartData));
  };

  useEffect(() => {
    let cart = [];
    if (localStorage.getItem('cart')) {
      cart = JSON.parse(localStorage.getItem('cart')).map((cartItem) => {
        return synchronizedSingleCartData({ item: cartItem, isProduct: cartItem.isProduct || false });
      });
    }

    if (faqs.length > 0) {
      setFaqItem(faqs);
    }
    handleDeliveryType(deliveryType);
    setCart(cart);
  }, []);

  const handleCollaps = (currentSelected) => {
    setOpenedCollapsibles((prev) => {
      if (prev.includes(currentSelected)) {
        return prev.filter((item) => item !== currentSelected);
      }
      return [...prev, currentSelected];
    });
    trackSectionToggle({
      page_type: 'course_detail',
      course_id: courseId,
      section_name: currentSelected,
      state: openedCollapsibles.includes(currentSelected) ? 'closed' : 'opened',
    });
  };

  const onClickOpenModalCourse = (item) => {
    setSelectedCourse(item);
    setOpenModalCourse(true);
  };

  const handlePaginationBundle = (page) => {
    setShowedCourse(listCourse.slice(pagination.per_page * (page - 1), pagination.per_page * page));
    setPagination({
      ...pagination,
      current_page: page,
    });
  };

  const classNameByLang = getClassNameByLang(language);

  return (
    <>
      <Head>
        <title>{courseTitle}</title>
        <meta property="og:title" content={courseTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={picture} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="product" />
      </Head>

      <div className="course-description-breadcrumb">
        {isLoading ? (
          <div className="loading">
            <Spinner animation="border" variant="white" style={{ width: '50px', height: '50px' }} />
          </div>
        ) : (
          ''
        )}
        <Breadcrumb
          language={language}
          dir={getDirectionByLanguage(language)}
          data={[
            { title: 'Home', url: '/' },
            { title: 'Courses', url: '/category?category=all' },
            { title: truncateText(courseTitle), active: true },
          ]}
          variant="no-padding"
        />
      </div>

      {/* Course Image for Mobile */}
      <div className="image-container mobile w-100 d-lg-none d-block ">
        <img src={generateCoverImageUrl(picture)} alt="course image" className="image w-100 h-100"></img>
      </div>

      <div className="course-description">
        <div className="course-description__overview">
          {/* Course title for mobile view */}
          <Heading
            variety={2}
            className={`d-lg-none d-block title-1 mb-3 ${classNameByLang}`}
            style={{ textWrap: 'break-word' }}
          >
            {courseTitle}
            {/* {truncateText(courseTitle)} */}
          </Heading>

          {/* COurse provider logo */}
          {handleIsImageAvailable(courseOrganizer?.logo) ? (
            <div className="user-image">
              <div className="user-image-container">
                <img src={currentImage} onError={() => handleImageToDefault()} />
              </div>
            </div>
          ) : (
            <div className="user-image">
              <p className="title-2">{courseOrganizer?.name}</p>
              <div className="user-image-container">
                <img src={currentImage} onError={() => handleImageToDefault()} />
              </div>
            </div>
          )}

          {/* Course title for desktop view */}
          <Heading
            variety={2}
            dir={getDirectionByLanguage(language)}
            className={`d-none d-lg-block title-1 ${classNameByLang}`}
          >
            {courseTitle}
          </Heading>

          {/* Detail Course Card for Mobile */}
          <div className="d-lg-none d-block border-bottom">
            <CourseData
              discount={discount}
              courseOrganizer={courseOrganizer?.code}
              price={price}
              learningProgram={learningProgram}
              categoryName={categoryName}
              subCategoryName={subCategoryName}
              levelCourse={levelCourse}
              delivery={delivery}
              certificateAwarded={certificateAwarded}
              facilitator={facilitator}
              courseDuration={courseDuration}
              scheduleTime={scheduleTime}
              scheduleDate={scheduleDate}
              timezone={timezone}
              language={language}
              month={month}
              isMobile
              isBundling={isBundling}
              totalCourseBundling={listCourse.length || 0}
              city={city}
            />
          </div>

          {/* Course Description */}
          <SubHeading variety={isMobile ? 2 : 3} className="title-2 mt-4 mt-lg-0">
            Course Description
          </SubHeading>
          <TextBody
            variety={isMobile ? 2 : 4}
            dir={getDirectionByLanguage(language)}
            style={{ marginBottom: '6rem', textWrap: 'break-word' }}
            className={`descriptions ${classNameByLang}`}
            dangerouslySetInnerHTML={{ __html: description }}
          ></TextBody>

          {/* Collapsible Section of Description */}
          <CollapsibleSection
            courseOrganizer={courseOrganizer}
            purchaseOption={purchaseOption}
            coursOutlineFileName={coursOutlineFileName}
            courseOutline={courseOutline}
            expectedOutcomes={expectedOutcomes}
            courseAudience={courseAudience}
            isBundling={isBundling}
            courseInclusion={courseInclusion}
            openedCollapsibles={openedCollapsibles}
            prerequisite={prerequisite}
            handleCollaps={handleCollaps}
            language={language}
          />
          <p></p>
        </div>

        {/* Testimoni Section of COurse Description */}
        {courseTestimoni && courseTestimoni.length > 0 ? (
          <div className="course-description__testimonial">
            <TestimonialSection
              courseTestimoni={courseTestimoni}
              customerActive={customerActive}
              totalShowedTestimony={totalShowedTestimony}
              lastIndexShownTesti={lastIndexShownTesti}
              setCustomerActive={setCustomerActive}
              setLastIndexShownTesti={setLastIndexShownTesti}
              language={language}
            />
          </div>
        ) : null}

        {/* FAQ Section of Course Description */}
        {faqItem.length > 0 && (
          <>
            <div
              className="d-flex align-items-center justify-content-between flex-row w-100 pointer course-description__faq"
              onClick={() => setOpenFaqItem((prev) => !prev)}
            >
              <SubHeading variety={3} className="mb-2">
                Frequently Asked Questions
              </SubHeading>
              <img src={openFaqItem ? ICONS.chevronUp : ICONS.chevronDown} alt="chevronDown" />
            </div>
            <div className="course-description__faq-items">
              {faqItem.length > 0 &&
                openFaqItem &&
                faqItem.map((row, index) => (
                  <Accordion
                    key={row.index}
                    question={row.question}
                    classNameByLang={classNameByLang}
                    answer={row.answer}
                    isOpen={row.isOpen}
                    onClick={() => {
                      setFaqItem((prev) =>
                        prev.map((item, idx) => ({
                          ...item,
                          isOpen: idx === index ? !item.isOpen : false,
                        }))
                      );
                    }}
                  />
                ))}
            </div>
          </>
        )}

        {/* If course is not bundling, show recommended course */}
        <div className="course-description__recomended">
          <SubHeading variety={isMobile ? 2 : 3} className="mb-0">
            {!isBundling
              ? `Recommended ${courseRecommendation.length > 1 ? 'Courses' : 'Course'}`
              : `${listCourse.length === 0 ? '' : listCourse.length > 1 ? 'Courses' : 'Course'}`}
          </SubHeading>
          <div className="course-container-not-bundling p-2">
            {!isBundling &&
              courseRecommendation &&
              courseRecommendation.slice(0, recommendedExpanded ? courseRecommendation.length : 2).map((data) => {
                const {
                  _id,
                  courseTitle,
                  discount,
                  price,
                  courseOrganizer,
                  description,
                  courseSlug,
                  picture,
                  language,
                } = data;

                return (
                  <RecommendedCourses
                    key={_id}
                    itemId={_id}
                    picture={picture}
                    courseOrganizer={courseOrganizer}
                    courseTitle={courseTitle}
                    description={description}
                    discount={discount}
                    price={price}
                    courseSlug={courseSlug}
                    language={language}
                  />
                );
              })}
          </div>
          {!isBundling && courseRecommendation?.length - 2 > 0 && (
            <SubHeading variety={4} onClick={() => setIsRecommendedExpanded((prev) => !prev)} className="show-more">
              {recommendedExpanded ? 'Show Less' : 'Show More'}
            </SubHeading>
          )}
        </div>

        <div className="course-description__checkout">
          {/* Detail Course Card for Desktop */}
          <div className="card-floating description-checkout">
            {/* <CardCheckoutImage> */}
            {/* <img src={ICONS.playButton} alt="icon-button" /> */}
            <div className="image-container d-none d-lg-block">
              <img src={generateCoverImageUrl(picture)} alt="course image" className="image"></img>
            </div>
            {/* </CardCheckoutImage> */}
            <div className="d-none d-lg-block">
              <CourseData
                discount={discount}
                courseOrganizer={courseOrganizer?.code}
                price={price}
                learningProgram={learningProgram}
                categoryName={categoryName}
                subCategoryName={subCategoryName}
                levelCourse={levelCourse}
                delivery={delivery}
                certificateAwarded={certificateAwarded}
                facilitator={facilitator}
                courseDuration={courseDuration}
                scheduleTime={scheduleTime}
                scheduleDate={scheduleDate}
                timezone={timezone}
                language={language}
                month={month}
                isBundling={isBundling}
                totalCourseBundling={listCourse.length || 0}
                city={city}
              />
            </div>
            <Button
              variety={isMobile ? 3 : 4}
              fontType={FONT_TYPES.subHeading}
              type={isAddedToCart ? BUTTON_TYPE.addedToCart : BUTTON_TYPE.primary}
              onClick={() => handleAddToCart(courseDetail)}
            >
              {isAddedToCart ? 'Remove from cart' : 'Add to cart'}
            </Button>
          </div>
        </div>
      </div>

      {/* If course is bundling, show the courses inside the bundle */}
      <Row className="g-5 course-bundle-container">
        {isBundling &&
          showedCourse &&
          showedCourse.length > 0 &&
          showedCourse.map((data) => {
            return <CardForBundle data={data} key={data._id} onClick={() => onClickOpenModalCourse(data)} />;
          })}
      </Row>
      {pagination?.totals > 6 ? (
        <Row className="justify-content-end" style={{ padding: '0 4rem 4rem 4rem', width: '100%' }}>
          <div className="col-md-4">
            <CustomPagination
              pagination={pagination}
              showData={false}
              center={false}
              onChangePage={handlePaginationBundle}
            />
          </div>
        </Row>
      ) : (
        ''
      )}

      {/* Modal Checkout*/}
      {openModalCheckout && (
        <ModalCheckout
          setOpen={setOpenModalCheckout}
          open={openModalCheckout}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          language={language}
        />
      )}

      {/* modal Course */}
      {openModalCourse && (
        <ModalCourse
          data={selectedCourse}
          setOpen={setOpenModalCourse}
          open={openModalCourse}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
      )}

      {providerMismatch && (
        <ModalWrapper open={providerMismatch} setOpen={setProviderMismatch}>
          <div className="provider-error-popup gap-4">
            <img src="/error-icon.gif" width={200} height={200} alt="Error Gif" />
            <Heading variety={3} style={{ color: '#066BFB' }}>
              Oops!!!
            </Heading>
            <TextBody variety={3} className="mb-3 text-center">
              It looks like you’ve taken a wrong turn. We couldn’t find the course you’re looking for here.
            </TextBody>
            <Button
              type={BUTTON_TYPE.primary}
              className="mb-4"
              onClick={() => {
                window.location.href = `https://${currentProvider}.${baseDomain}`;
              }}
              variety={5}
              fontType={FONT_TYPES.subHeading}
            >
              Back to Home Page
            </Button>
          </div>
        </ModalWrapper>
      )}
    </>
  );
};

export default CourseDescription;
